import { Search } from "lucide-react";

import { EntryTable } from "@/components/entries/EntryTable";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOnBehalfViewModel } from "@/pages/dashboard/admin/useOnBehalfViewModel";

export function OnBehalfView() {
  const vm = useOnBehalfViewModel();
  const {
    user, isAdmin, isManagerOnly, canCreateOnBehalf, canEditPunches,
    onBehalfOpen, setOnBehalfOpen,
    onBehalfForm, setOnBehalfForm,
    obEmployeeSearch, setObEmployeeSearch, obEmployeeOpen, setObEmployeeOpen, obEmployeeRef,
    obCostCodeSearch, setObCostCodeSearch, obCostCodeOpen, setObCostCodeOpen, obCostCodeRef,
    obCustomerSearch, setObCustomerSearch, obCustomerOpen, setObCustomerOpen, obCustomerRef,
    onBehalfUsersData, onBehalfCostCodesData, onBehalfCustomersData,
    resetOnBehalfForm, createOnBehalfMutation,
    createdPunches,
  } = vm;
  return (
    <>
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
                {canCreateOnBehalf && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle>Create Punch for Employee</CardTitle>
                        <CardDescription>
                          {isManagerOnly
                            ? "Create a missed or corrected punch for someone on your team. Same rules as a normal punch (duration, OT, open-punch limits)."
                            : "Create a punch on behalf of any employee. Same rules as a normal punch (duration, OT, open-punch limits)."}
                        </CardDescription>
                      </div>
                      <Button type="button" onClick={() => setOnBehalfOpen(true)}>
                        Create Punch for Employee
                      </Button>
                    </CardHeader>
                    {createdPunches.length > 0 && (
                      <CardContent>
                        <div className="mb-2 text-sm text-muted-foreground">
                          Created this session ({createdPunches.length}). Not saved as a separate history — refresh clears this list.
                        </div>
                        <EntryTable
                          entries={createdPunches}
                          compact
                          currentUserId={user?.id}
                          canEditPunches={canEditPunches}
                          canDeletePunches={!!isAdmin}
                        />
                      </CardContent>
                    )}
                  </Card>
                )}
    </div>
      <Dialog
        open={onBehalfOpen}
        onOpenChange={(open) => {
          setOnBehalfOpen(open);
          if (!open) {
            resetOnBehalfForm();
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Punch for Employee</DialogTitle>
            <DialogDescription>
              Enter clock-in (required) and optional clock-out. Times use America/Detroit (company standard) unless the device provides another zone.
              Search to pick employee, cost code, and site.
              {isManagerOnly ? " You can only select people on your team." : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1 min-w-0">
            <div className="grid gap-1.5 min-w-0">
              <Label htmlFor="ob-employee">Employee</Label>
              <div className="relative min-w-0" ref={obEmployeeRef}>
                <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
                <Input
                  id="ob-employee"
                  data-testid="ob-employee-search"
                  className="pl-9"
                  value={
                    obEmployeeOpen
                      ? obEmployeeSearch
                      : onBehalfForm.employeeLabel || ""
                  }
                  onChange={(e) => {
                    setObEmployeeSearch(e.target.value);
                    if (!obEmployeeOpen) setObEmployeeOpen(true);
                  }}
                  onFocus={() => {
                    setObEmployeeSearch("");
                    setObEmployeeOpen(true);
                  }}
                  placeholder={obEmployeeOpen ? "Search by name or email…" : "Select employee"}
                  autoComplete="off"
                />
                {obEmployeeOpen && (
                  <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                    {(onBehalfUsersData ?? []).map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        data-testid={`ob-employee-option-${u.id}`}
                        className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                        onClick={() => {
                          setOnBehalfForm((f) => ({
                            ...f,
                            employeeId: String(u.id),
                            employeeLabel: u.email
                              ? `${u.full_name} (${u.email})`
                              : u.full_name,
                          }));
                          setObEmployeeOpen(false);
                          setObEmployeeSearch("");
                        }}
                      >
                        <div className="font-medium break-words">{u.full_name}</div>
                        <div className="text-xs text-muted-foreground break-words">{u.email}</div>
                      </button>
                    ))}
                    {(onBehalfUsersData ?? []).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="ob-in">
                  Clock in <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ob-in"
                  data-testid="ob-clock-in"
                  type="datetime-local"
                  value={onBehalfForm.clockIn}
                  onChange={(e) => setOnBehalfForm((f) => ({ ...f, clockIn: e.target.value }))}
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ob-out">Clock out (optional)</Label>
                <Input
                  id="ob-out"
                  data-testid="ob-clock-out"
                  type="datetime-local"
                  value={onBehalfForm.clockOut}
                  onChange={(e) => setOnBehalfForm((f) => ({ ...f, clockOut: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-3 min-w-0">
              <div className="grid gap-1.5 min-w-0">
                <Label htmlFor="ob-cc">
                  Cost code <span className="text-destructive">*</span>
                </Label>
                <div className="relative min-w-0" ref={obCostCodeRef}>
                  <Input
                    id="ob-cc"
                    data-testid="ob-cost-code-search"
                    value={
                      obCostCodeOpen
                        ? obCostCodeSearch
                        : onBehalfForm.costCodeLabel || ""
                    }
                    onChange={(e) => {
                      setObCostCodeSearch(e.target.value);
                      if (!obCostCodeOpen) setObCostCodeOpen(true);
                    }}
                    onFocus={() => {
                      setObCostCodeSearch("");
                      setObCostCodeOpen(true);
                    }}
                    placeholder={obCostCodeOpen ? "Search cost codes…" : "Select cost code"}
                    autoComplete="off"
                    required
                  />
                  {obCostCodeOpen && (
                    <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                      {(onBehalfCostCodesData ?? []).map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`ob-cost-code-option-${c.id}`}
                          className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                          onClick={() => {
                            setOnBehalfForm((f) => ({
                              ...f,
                              costCodeId: String(c.id),
                              costCodeLabel: c.general_ledger
                                ? `${c.cost_code} (${c.general_ledger})`
                                : c.cost_code,
                            }));
                            setObCostCodeOpen(false);
                            setObCostCodeSearch("");
                          }}
                        >
                          {c.cost_code} {c.general_ledger ? `(${c.general_ledger})` : ""}
                        </button>
                      ))}
                      {(onBehalfCostCodesData ?? []).length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid gap-1.5 min-w-0">
                <Label htmlFor="ob-cust">
                  Site / customer <span className="text-destructive">*</span>
                </Label>
                <div className="relative min-w-0" ref={obCustomerRef}>
                  <Input
                    id="ob-cust"
                    data-testid="ob-customer-search"
                    value={
                      obCustomerOpen
                        ? obCustomerSearch
                        : onBehalfForm.customerLabel || ""
                    }
                    onChange={(e) => {
                      setObCustomerSearch(e.target.value);
                      if (!obCustomerOpen) setObCustomerOpen(true);
                    }}
                    onFocus={() => {
                      setObCustomerSearch("");
                      setObCustomerOpen(true);
                    }}
                    placeholder={obCustomerOpen ? "Search customers…" : "Select site / customer"}
                    autoComplete="off"
                    required
                  />
                  {obCustomerOpen && (
                    <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                      {(onBehalfCustomersData ?? []).map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`ob-customer-option-${c.id}`}
                          className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                          onClick={() => {
                            const label = c.customer_id
                              ? `${c.company} (${c.customer_id})`
                              : c.company;
                            setOnBehalfForm((f) => ({
                              ...f,
                              customerId: String(c.id),
                              customerLabel: label,
                            }));
                            setObCustomerOpen(false);
                            setObCustomerSearch("");
                          }}
                        >
                          {c.company} {c.customer_id ? `(${c.customer_id})` : ""}
                        </button>
                      ))}
                      {(onBehalfCustomersData ?? []).length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ob-notes">Notes</Label>
              <Textarea
                id="ob-notes"
                data-testid="ob-notes"
                rows={3}
                value={onBehalfForm.notes}
                onChange={(e) => setOnBehalfForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Reason for creating this punch…"
              />
            </div>
            {onBehalfForm.error && (
              <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
                <div className="text-sm break-words">{onBehalfForm.error}</div>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOnBehalfOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="ob-submit"
              disabled={
                createOnBehalfMutation.isPending ||
                !onBehalfForm.employeeId ||
                !onBehalfForm.clockIn ||
                !onBehalfForm.costCodeId ||
                !onBehalfForm.customerId
              }
              onClick={() => {
                if (
                  !onBehalfForm.employeeId ||
                  !onBehalfForm.clockIn ||
                  !onBehalfForm.costCodeId ||
                  !onBehalfForm.customerId
                ) {
                  setOnBehalfForm((f) => ({
                    ...f,
                    error: "Employee, clock-in, cost code, and site / customer are required.",
                  }));
                  return;
                }
                // datetime-local → ISO-like local wall; backend attaches export_tz
                const clockIn = onBehalfForm.clockIn.length === 16 ? `${onBehalfForm.clockIn}:00` : onBehalfForm.clockIn;
                const clockOut =
                  onBehalfForm.clockOut
                    ? onBehalfForm.clockOut.length === 16
                      ? `${onBehalfForm.clockOut}:00`
                      : onBehalfForm.clockOut
                    : null;
                setOnBehalfForm((f) => ({ ...f, error: "" }));
                createOnBehalfMutation.mutate({
                  employee_user_id: Number(onBehalfForm.employeeId),
                  clock_in_time: clockIn,
                  clock_out_time: clockOut,
                  tz_name: "America/Detroit",
                  cost_code_id: Number(onBehalfForm.costCodeId),
                  customer_id: Number(onBehalfForm.customerId),
                  notes: onBehalfForm.notes || null,
                });
              }}
            >
              {createOnBehalfMutation.isPending ? "Creating…" : "Create punch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
